import {
  ActivePeriodsFilterConfigColumn,
  AvgSessionLengthFilterConfigColumn,
  CohortFilterConfigColumn,
  ConversationCategoryFilterConfigColumn,
  ConversationLabelFilterConfigColumn,
  ConversationSentimentFilterConfigColumn,
  ConversationTopicFilterConfigColumn,
  ConversationUserActionFilterConfigColumn,
  EmotionsFilterConfigColumn,
  FeedbackTypeV2FilterConfigColumn,
  FilterCategoryConfigColumn,
  FilterConversationIdKind,
  FilterConversationLabelKind,
  FilterConversationTopicKind,
  FilterConversationUserActionKind,
  FilterConversationUserQueryKind,
  FilterDataSourceKind,
  FilterEmotionKind,
  FilterConversationErrorStatusKind,
  FilterInputOutputKind,
  FilterInteractionIdConfigColumn,
  FilterInteractionIdKind,
  FilterInteractionLabelKind,
  FilterIsNegativeIntentV2Kind,
  FilterKeywordKind,
  FilterLanguageKind,
  FilterNegativeUserIntentV2Kind,
  FilterPiiKind,
  FilterSentimentKind,
  FilterSessionLengthConfigColumn,
  FilterSessionLengthKind,
  FilterTopicKind,
  FilterTypeOfProblemV2Kind,
  FilterUserActionKind,
  FilterUserFeedbackV2Kind,
  FilterUserIntentKind,
  FilterUserKind,
  FilterUserQueryKind,
  FirstInteractionFilterConfigColumn,
  type GetInteractionAggregatesReqFiltersItem,
  type GetInteractionAggregatesReqFiltersV2Item,
  GroupByCohortKind,
  GroupByConversationCategoryKind,
  GroupByConversationIdKind,
  GroupByConversationLabelKind,
  GroupByConversationSentimentKind,
  GroupByConversationTopicKind,
  GroupByConversationUserActionKind,
  GroupByConversationUserQueryKind,
  GroupByDataSourceKind,
  GroupByEmotionsKind,
  GroupByConversationErrorStatusKind,
  GroupByKeywordKind,
  GroupByLanguageKind,
  GroupByInteractionLabelKind,
  GroupByNegativeUserIntentV2Kind,
  GroupByPiiKind,
  GroupByPositiveCommentKind,
  GroupBySentimentKind,
  GroupByTopicKind,
  GroupByTypeOfProblemV2Kind,
  GroupByUserActionKind,
  GroupByUserFeedbackV2Kind,
  GroupByUserIntentKind,
  GroupByUserKind,
  IntentFilterConfigColumn,
  InteractionLabelFilterConfigColumn,
  LanguageFilterConfigColumn,
  LastInteractionFilterConfigColumn,
  LastInteractionTagFilterConfigColumn,
  NegativeIntentV2FilterConfigColumn,
  PiiFilterConfigColumn,
  SentimentFilterConfigColumn,
  SharedByFilterConfigKind,
  SourceFilterConfigColumn,
  KeywordFilterConfigColumn,
  TopicFilterConfigColumn,
  TypeOfProblemV2ConfigColumn,
  UserActionFilterConfigColumn,
  UserFilterConfigColumn,
  UsersInteractionsFilterConfigColumn,
  UserConversationsCountFilterConfigColumn,
  UserConversationLengthFilterConfigColumn,
  FilterHasUserCommentKind,
  FilterUserRatingKind,
  FilterTypeOfRiskKind,
  GroupByCategoryKind,
  FilterUserInteractionPositionConfigColumn,
  ConversationErrorStatusFilterConfigColumn,
} from '@/api/generated/model';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';

export const CONVERSATION_PREFIX = 'conversation_';
export const TAG_PREFIX = 'tag:';
export const CUSTOM_AGGREGATE_PREFIX = 'custom_aggregate:';
export const CONVERSATION_CUSTOM_AGGREGATE_PREFIX = 'conversation_custom_aggregate:';
export const CUSTOM_COLUMN_PREFIX = 'custom:';
export const CONVERSATION_CUSTOM_COLUMN_PREFIX = 'conversation_custom:';

export const addTagPrefix = (tag: string) => `${TAG_PREFIX}${tag}`;
export const addColumnPrefix = (column: string) => `${CUSTOM_COLUMN_PREFIX}${column}`;
export const addCustomAggregatePrefix = (str: string, aggregateBy: AggregateBy) => {
  if (isCustomAggregate(str)) {
    const stripped = removeCustomAggregatePrefix(str);
    return aggregateBy === 'interaction'
      ? `${CUSTOM_AGGREGATE_PREFIX}${stripped}`
      : `${CONVERSATION_CUSTOM_AGGREGATE_PREFIX}${stripped}`;
  }

  return aggregateBy === 'interaction'
    ? `${CUSTOM_AGGREGATE_PREFIX}${str}`
    : `${CONVERSATION_CUSTOM_AGGREGATE_PREFIX}${str}`;
};

export function isConversation(str: string) {
  return str.startsWith(CONVERSATION_PREFIX);
}

export function removeConversationPrefix(str: string) {
  return str.replace(CONVERSATION_PREFIX, '');
}

export function isTag(str: string) {
  return str.startsWith(TAG_PREFIX);
}

export function isCustomAggregate(str: string) {
  return str.startsWith(CUSTOM_AGGREGATE_PREFIX) || str.startsWith(CONVERSATION_CUSTOM_AGGREGATE_PREFIX);
}

export function removeCustomAggregatePrefix(str: string) {
  if (str.startsWith(CUSTOM_AGGREGATE_PREFIX)) {
    return str.replace(CUSTOM_AGGREGATE_PREFIX, '');
  }
  if (str.startsWith(CONVERSATION_CUSTOM_AGGREGATE_PREFIX)) {
    return str.replace(CONVERSATION_CUSTOM_AGGREGATE_PREFIX, '');
  }

  return str;
}

export function removeCustomAggregatePrefixFromFilters(str: string) {
  if (str.startsWith(CUSTOM_AGGREGATE_PREFIX)) {
    return str.replace(CUSTOM_AGGREGATE_PREFIX, '');
  }
  if (str.startsWith(CONVERSATION_CUSTOM_AGGREGATE_PREFIX)) {
    return str.replace(CONVERSATION_CUSTOM_AGGREGATE_PREFIX, 'Conversation ');
  }

  return str;
}

export function removeTagPrefix(str: string) {
  return str.replace(TAG_PREFIX, '');
}

export function removeAllPrefixes(str: string) {
  return removeConversationPrefix(removeCustomAggregatePrefix(removeTagPrefix(str)));
}

export function isCustomColumn(str: string) {
  return str.startsWith(CUSTOM_COLUMN_PREFIX);
}

export function isConversationCustomColumn(str: string) {
  return str.startsWith(CONVERSATION_CUSTOM_COLUMN_PREFIX);
}

// ====================================================
// FILTERS
// ====================================================
export const FILTERS = {
  columnTopic: TopicFilterConfigColumn.topic,
  columnCategory: FilterCategoryConfigColumn.category,
  columnConversationCategory: ConversationCategoryFilterConfigColumn.conversation_category,
  columnLanguage: LanguageFilterConfigColumn.language,
  columnDataSource: SourceFilterConfigColumn.data_source,
  columnUser: UserFilterConfigColumn.user,
  columnPii: PiiFilterConfigColumn.pii,
  columnCohort: CohortFilterConfigColumn.cohort,
  columnIntent: IntentFilterConfigColumn.user_intent,
  columnUserAction: UserActionFilterConfigColumn.user_action,
  columnNegativeIntentV2: NegativeIntentV2FilterConfigColumn.negative_user_intent_v2,
  columnSubject: KeywordFilterConfigColumn.keyword,
  columnFeedbackTypeV2: FeedbackTypeV2FilterConfigColumn.user_feedback_v2,
  columnSentiment: SentimentFilterConfigColumn.sentiment,
  columnConversationSentiment: ConversationSentimentFilterConfigColumn.conversation_sentiment,
  columnEmotions: EmotionsFilterConfigColumn.emotions,
  columnConversationTopic: ConversationTopicFilterConfigColumn.conversation_topic,
  columnConversationUserAction: ConversationUserActionFilterConfigColumn.conversation_user_action,
  columnConversationErrorStatus: ConversationErrorStatusFilterConfigColumn.conversation_error_status,
  columnTypeOfProblemV2: TypeOfProblemV2ConfigColumn.type_of_problem_v2,
  columnSessionLength: FilterSessionLengthConfigColumn.conversation_session_length,
  columnInteractionId: FilterInteractionIdConfigColumn.interaction_id,
  columnInteractionLabel: InteractionLabelFilterConfigColumn.interaction_label,
  columnConversationLabel: ConversationLabelFilterConfigColumn.conversation_label,
  columnTag: 'tag',
  columnTagStartsWith: TAG_PREFIX,
  firstInteraction: FirstInteractionFilterConfigColumn.first_interaction,
  lastInteraction: LastInteractionFilterConfigColumn.last_interaction,
  lastInteractionTag: LastInteractionTagFilterConfigColumn.last_interaction_tag,
  usersInteraction: UsersInteractionsFilterConfigColumn.users_interactions,
  userConversationsCount: UserConversationsCountFilterConfigColumn.user_conversations_count,
  userConversationLength: UserConversationLengthFilterConfigColumn.user_conversation_length,
  activePeriods: ActivePeriodsFilterConfigColumn.active_periods,
  avgSessionLength: AvgSessionLengthFilterConfigColumn.avg_session_length,
  // columnTag: string
  // === kind ===
  kindUserIntent: FilterUserIntentKind.user_intent,
  kindNegativeUserIntentV2: FilterNegativeUserIntentV2Kind.negative_user_intent_v2,
  kindKeyword: FilterKeywordKind.keyword,
  kindUserFeedbackV2: FilterUserFeedbackV2Kind.user_feedback_v2,
  kindTypeOfProblemV2: FilterTypeOfProblemV2Kind.type_of_problem_v2,
  kindTypeOfRisk: FilterTypeOfRiskKind.type_of_risk,
  kindTopic: FilterTopicKind.topic,
  kindUser: FilterUserKind.user,
  kindUserQuery: FilterUserQueryKind.user_query,
  kindDataSource: FilterDataSourceKind.data_source,
  kindLanguage: FilterLanguageKind.language,
  kindPii: FilterPiiKind.pii,
  kindInputOutput: FilterInputOutputKind.input_output,
  kindIsNegativeIntentV2: FilterIsNegativeIntentV2Kind.is_negative_intent_v2,
  kindSentiment: FilterSentimentKind.sentiment,
  kindEmotions: FilterEmotionKind.emotions,
  kindErrorStatus: FilterConversationErrorStatusKind.conversation_error_status,
  kindConversationId: FilterConversationIdKind.conversation_id,
  kindInteractionId: FilterInteractionIdKind.interaction_id,
  kindInteractionLabel: FilterInteractionLabelKind.interaction_label,
  kindConversationLabel: FilterConversationLabelKind.conversation_label,
  kindConversationTopic: FilterConversationTopicKind.conversation_topic,
  kindConversationUserAction: FilterConversationUserActionKind.conversation_user_action,
  kindConversationUserQuery: FilterConversationUserQueryKind.conversation_user_query,
  kindSessionLength: FilterSessionLengthKind.conversation_session_length,
  kindUserAction: FilterUserActionKind.user_action,
  kindTag: 'tag',
  customAggregate: 'custom_aggregate',
  conversationCustomAggregate: 'conversation_custom_aggregate',
  kindTagStartsWith: TAG_PREFIX,
  kindHasUserComment: FilterHasUserCommentKind.has_user_comment,
  kindSharedBy: SharedByFilterConfigKind.shared_by,
  kindUserRating: FilterUserRatingKind.user_rating,
  columnUserInteractionPosition: FilterUserInteractionPositionConfigColumn.user_interaction_position,
  // tag, which is a string type
  // kindTag: string
} as const;

/** Filter columns routed to `special_filters` instead of `filters` on aggregate APIs. */
export const SPECIAL_FILTER_COLUMNS = [
  FILTERS.firstInteraction,
  FILTERS.lastInteraction,
  FILTERS.usersInteraction,
  FILTERS.userConversationsCount,
  FILTERS.userConversationLength,
  FILTERS.lastInteractionTag,
  FILTERS.activePeriods,
  FILTERS.avgSessionLength,
] as const;

export const isSpecialFilter = (column: string): boolean => {
  return (SPECIAL_FILTER_COLUMNS as readonly string[]).includes(column);
};

// get all filters that start with 'column'
export type AllFilterValue = (typeof FILTERS)[keyof typeof FILTERS];
export type FilterColumnValue = GetInteractionAggregatesReqFiltersItem['column'];
export type FilterKindValue = GetInteractionAggregatesReqFiltersV2Item['kind'];

export function isFilterDefined(filter: GenericFilter) {
  if (!filter.column) return false;

  if (filter.column === FILTERS.lastInteractionTag) {
    const tagFilter = filter as GenericFilter & { tag?: string; search?: string };
    return (
      !!tagFilter.tag &&
      (tagFilter.select_all || (Array.isArray(filter.values) && filter.values.length > 0) || !!tagFilter.search?.trim())
    );
  }

  if (isSpecialFilter(filter.column)) {
    return typeof filter.values === 'object' && (filter.values !== null || !!filter.select_all);
  }

  if (!filter.values) return false;

  return (
    filter.select_all ||
    (Array.isArray(filter.values) && filter.values.length > 0 && filter.values.find((v) => !!v)) ||
    (filter as any)?.search //eslint-disable-line @typescript-eslint/no-explicit-any
  );
}

export function isValidFilter(val: unknown): val is AllFilterValue {
  return (
    (Object.values(FILTERS) as unknown[]).includes(val) ||
    isTag(val as string) ||
    isCustomAggregate(val as string) ||
    isCustomColumn(val as string)
  );
}

// ====================================================
// GROUP_BYs / ADDITIONAL_GROUP_BYs
// ====================================================

export const GROUPBY_VALUES = {
  userIntent: GroupByUserIntentKind.user_intent,
  negativeUserIntentV2: GroupByNegativeUserIntentV2Kind.negative_user_intent_v2,
  keyword: GroupByKeywordKind.keyword,
  userFeedbackV2: GroupByUserFeedbackV2Kind.user_feedback_v2,
  dataSource: GroupByDataSourceKind.data_source,
  language: GroupByLanguageKind.language,
  pii: GroupByPiiKind.pii,
  typeOfProblemV2: GroupByTypeOfProblemV2Kind.type_of_problem_v2,
  topic: GroupByTopicKind.topic,
  user: GroupByUserKind.user,
  cohort: GroupByCohortKind.cohort,
  positiveComment: GroupByPositiveCommentKind.positive_comment,
  emotions: GroupByEmotionsKind.emotions,
  conversationErrorStatus: GroupByConversationErrorStatusKind.conversation_error_status,
  sentiment: GroupBySentimentKind.sentiment,
  conversationSentiment: GroupByConversationSentimentKind.conversation_sentiment,
  conversation: GroupByConversationIdKind.conversation_id,
  interactionLabel: GroupByInteractionLabelKind.interaction_label,
  conversationLabel: GroupByConversationLabelKind.conversation_label,
  conversationUserQuery: GroupByConversationUserQueryKind.conversation_user_query,
  conversationTopic: GroupByConversationTopicKind.conversation_topic,
  conversationCategory: GroupByConversationCategoryKind.conversation_category,
  conversationUserAction: GroupByConversationUserActionKind.conversation_user_action,
  userAction: GroupByUserActionKind.user_action,
  category: GroupByCategoryKind.category,
  tag: 'tag',
  customAggregate: 'custom_aggregate',
  conversationCustomAggregate: 'conversation_custom_aggregate',
  tagStartsWith: TAG_PREFIX,
  // tag: string,
} as const;

export type GroupByValue_internal = (typeof GROUPBY_VALUES)[keyof typeof GROUPBY_VALUES] | `tag:${string}`;
